/**
 * Sentry 辅助函数
 * 用于数据清理、格式化等
 */

/**
 * 清理数据对象中的敏感字段
 * @param {*} data - 要清理的数据
 * @returns {*} 清理后的数据
 */
export const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  // 敏感字段列表
  const sensitiveFields = [
    // 密码相关
    'password', 'pwd', 'passwd', 'newPassword', 'oldPassword', 'confirmPassword',
    // Token 相关
    'token', 'accessToken', 'refreshToken', 'v-token', 'vToken',
    'authorization', 'auth',
    // 密钥相关
    'secret', 'apiKey', 'secretKey', 'privateKey',
    // 证件号相关
    'idCard', 'identityCard', 'idNo', 'idNumber',
    'creditCard', 'creditCardNo',
    'bankCard', 'bankCardNo', 'bankAccount', 'accountNo',
    // 其他敏感信息
    'cvv', 'securityCode',
    'ssn', 'taxId',
  ];
  
  // 递归清理
  const sanitized = Array.isArray(data) ? [...data] : { ...data };
  
  for (const key in sanitized) {
    const lowerKey = key.toLowerCase();
    
    // 检查是否为敏感字段
    const isSensitive = sensitiveFields.some(field => 
      lowerKey.includes(field.toLowerCase())
    );
    
    if (isSensitive) {
      sanitized[key] = '[FILTERED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      // 递归处理嵌套对象
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  }
  
  return sanitized;
};

/**
 * 生成请求唯一 ID
 * @returns {String} 请求 ID
 */
export const generateRequestId = () => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 格式化 URL（移除敏感参数）
 * @param {String} url - 原始 URL
 * @returns {String} 清理后的 URL
 */
export const sanitizeUrl = (url) => {
  try {
    const urlObj = new URL(url, window.location.origin);
    const sensitiveParams = ['token', 'password', 'pwd', 'secret', 'key', 'auth'];
    
    sensitiveParams.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[FILTERED]');
      }
    });
    
    return urlObj.toString();
  } catch (e) {
    return url;
  }
};

/**
 * 截断长字符串
 * @param {String} str - 字符串
 * @param {Number} maxLength - 最大长度
 * @returns {String} 截断后的字符串
 */
export const truncateString = (str, maxLength = 1000) => {
  if (typeof str !== 'string') return str;
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '... (truncated)';
};

/**
 * 获取错误指纹（用于错误分组）
 * @param {String} errorType - 错误类型
 * @param {String} url - 请求 URL
 * @param {Number} statusCode - 状态码
 * @returns {Array} 指纹数组
 */
export const getErrorFingerprint = (errorType, url, statusCode) => {
  // 移除 URL 中的动态参数
  const normalizedUrl = url.replace(/\/\d+/g, '/:id').replace(/\?.*$/, '');
  return [errorType, normalizedUrl, String(statusCode)];
};

/**
 * 判断是否为敏感 URL
 * @param {String} url - URL
 * @returns {Boolean} 是否敏感
 */
export const isSensitiveUrl = (url) => {
  const sensitivePatterns = [
    '/login',
    '/register',
    '/password',
    '/token',
    '/auth',
  ];
  
  return sensitivePatterns.some(pattern => url.includes(pattern));
};

/**
 * 获取简化的错误信息
 * @param {Error} error - 错误对象
 * @returns {Object} 简化的错误信息
 */
export const getSimplifiedError = (error) => {
  if (!error) return null;
  
  return {
    message: error.message || 'Unknown error',
    code: error.code,
    name: error.name,
    // 只保留前 5 行堆栈信息
    stack: error.stack ? error.stack.split('\n').slice(0, 5).join('\n') : null,
  };
};

export default {
  sanitizeData,
  generateRequestId,
  sanitizeUrl,
  truncateString,
  getErrorFingerprint,
  isSensitiveUrl,
  getSimplifiedError,
};

